import { signIn, signOut } from "@/utils/auth/client";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";

export const useLoginMutation = (setIsLoading?: (loading: boolean) => void, setError?: (error: string) => void) => {
    const navigate = useNavigate();
    return useMutation({
        mutationFn: async (credentials: { email: string; password: string }) => {
            const result = await signIn.email(credentials, {
                onSuccess: (context) => {
                    // Check if 2FA redirect is needed
                    if (context.data.twoFactorRedirect) {
                        throw new Error("twoFactorRedirect");
                    }
                },
            });

            if (result.error) {
                throw result.error;
            }

            return result;
        },
        onMutate: async () => {
            if (setIsLoading) setIsLoading(true);
        },
        onSuccess: async () => {
            navigate("/");
        },
        onError: (error: any) => {
            console.log("Login error:", error);
            
            // Extract error message
            let errorMessage = "An error occurred during login";
            
            if (error.message) {
                // Check for ban/disabled account errors
                if (error.message.includes("disabled") || error.message.includes("banned")) {
                    errorMessage = error.message;
                } else if (error.message === "twoFactorRedirect") {
                    // Let this propagate for 2FA handling
                    errorMessage = error.message;
                } else if (error.message.includes("Invalid")) {
                    errorMessage = "Invalid email or password";
                } else {
                    errorMessage = error.message;
                }
            }
            
            if (setError) setError(errorMessage);
        },
        onSettled: async () => {
            if (setIsLoading) setIsLoading(false);
        },
    });
};

export const useLogoutMutation = (setIsLoading?: (loading: boolean) => void) => {
    return useMutation({
        mutationFn: async () => {
            const result = await signOut();

            // Check for errors in logout as well
            if (result.error) {
                throw result.error;
            }

            return result;
        },
        onMutate: async () => {
            return setIsLoading && setIsLoading(true);
        },
        onSuccess: async () => {
            window.location.href = "/login";
        },
        onError: (error: any) => {
            console.log("Logout error:", error);
            // For logout, we might still want to redirect even on error
            window.location.href = "/login";
        },
        onSettled: async () => {
            return setIsLoading && setIsLoading(false);
        },
    });
};
export const useMicrosoftSignInMutation = (
    setIsLoading?: (loading: boolean) => void,
    setError?: (error: string) => void
) => {
    return useMutation({
        mutationFn: async () => {
            console.log("Starting Microsoft sign-in...");

            try {
                // According to better-auth docs, social sign-in should redirect automatically
                // We should NOT await this as it redirects the browser
                signIn.social({
                    provider: "microsoft",
                    callbackURL: "/",
                });

                // If we reach this point, the redirect should have happened
                console.log("Microsoft sign-in initiated - should redirect to Microsoft");
            } catch (error) {
                console.error("Error initiating Microsoft sign-in:", error);
                throw error;
            }
        },
        onMutate: async () => {
            console.log("Microsoft sign-in mutation starting...");
            if (setIsLoading) setIsLoading(true);
        },
        onSuccess: async () => {
            console.log("Microsoft sign-in mutation completed - redirect should have happened");
            // Don't manually redirect here - better-auth handles it
        },
        onError: (error: any) => {
            console.error("Microsoft sign-in mutation error:", error);
            const errorMessage = error.message || "Microsoft sign-in failed";
            if (setError) setError(errorMessage);
        },
        onSettled: async () => {
            console.log("Microsoft sign-in mutation settled");
            if (setIsLoading) setIsLoading(false);
        },
    });
};
